<template>
  <div>
    <Spinner v-if="isLoading" />
    <div v-else-if="prospect">
      <div class="default-prospect-header">
        <div class="header-name">
          <h2 class="grand-size-text prospect">
            {{ prospect.name }} {{ prospect.lastName }}
          </h2>
          <span
            v-if="prospect.status"
            id="prospectCurrentStatus"
            class="current-status">
            {{ $t(`prospectStatus.${prospect.status}`) }}
            <i class="fas fa-chevron-right" />
            {{ $t(`prospectSubStatus.${prospect.subStatus}`) }}
          </span>
          <b-button
            v-if="prospect.brokerId"
            :id="`prospect-${prospect._id}`"
            class="is-button-secondary ml-2 is-small is-rounded is-fullwidth-mobile"
            @click="goToSingleBrokerView(prospect.brokerId)">
            {{ $t("accessBroker") }}
          </b-button>
          <b-button
            id="return-btn"
            class="is-button-secondary ml-2 is-small is-rounded is-pulled-right"
            @click="$router.go(-1)">
            {{ $t("back") }}
          </b-button>
        </div>
      </div>
      <div class="card-item prospect-info mb-3">
        <div class="prospect-content-buttons">
          <b-tabs v-model="selectedContent" expanded>
            <b-tab-item
              v-for="(value, content) in contentButtons"
              :id="`${value}-data`"
              :key="content"
              :label="$t(value)"
              :value="`${content}-data`" />
          </b-tabs>
        </div>
        <ProspectData v-if="selectedContent === 'prospect-data'" :prospect="prospect" />
        <SimulationsData v-if="selectedContent === 'simulations-data'" :simulations="simulations">
          <template
            v-if="showSimulationButton"
            #simulation-header>
            <div class="call-to-action mx-1 has-text-centered">
              <div class="is-6-widescreen is-full-desktop card-item is-shadowless has-background-light is-flex is-flex-wrap-wrap is-justify-content-space-between px-5 py-4">
                <p class="is-size-6 my-1 has-text-weight-medium pr-6">{{ $t("simulatorLead") }}</p>
                <div>
                  <b-button
                    id="add-simulation-button"
                    tag="a"
                    class="is-button-primary is-small is-rounded my-0"
                    icon-left="fa-solid fa-plus"
                    size="is-small"
                    :href="buildSimulatorFrontUrl()"
                    target="_blank">
                    {{ $t("simulator") }}
                  </b-button>
                </div>
              </div>
            </div>
          </template>
        </SimulationsData>
        <DocumentForm
          v-if="selectedContent === 'documents-data'"
          id="prospect-contact-data"
          :prospect-information-props="prospect" />
      </div>
      <InteractionsHistory
        :fields.sync="interactionFields"
        :interactions="prospect.interactions"
        :is-loading.sync="loadingInteractions"
        @create-new-interaction="createNewInteraction($event)"
        @load-interactions="loadProspect()" />
    </div>
  </div>
</template>

<script>
import { getSimulations } from '@/application/services/api-clients/simulations';
import {
  getProspectById,
  addInteractions,
  getProspectStatusList
} from '@/application/services/api-clients/prospects-leads';

import { leadInteractionFields } from '@/domain/constants/lead-interaction-fields';

import { dateToFormatedString } from '@/infrastructure/date';
import { formatNumberByCountry } from '@/infrastructure/format';

import Spinner from '@/ui/components/Spinner.vue';
import InteractionsHistory from '@/ui/components/interactions/InteractionsHistory.vue';
import DocumentForm from '@/ui/views/public/DocumentForm.vue';
import ProspectData from '@/ui/components/internal/leads/ProspectData.vue';
import SimulationsData from '@/ui/components/internal/leads/SimulationsData.vue';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';

import { convertObjectToBase64 } from '@/domain/helpers';
import locale from '@/ui/mixins/locale';

export default {
  name: 'LeadsDetails',
  components: { Spinner, InteractionsHistory, DocumentForm, ProspectData, SimulationsData },
  mixins: [locale],

  data() {
    const { prospectId } = this.$route.params;
    return {
      showSimulationButton: null,
      simulations: [],
      prospect: {},
      isLoading: false,
      prospectId,
      contentButtons: {
        prospect: 'contactTitle',
        simulations: 'simulationsTitle',
        documents: 'documentsTitle'
      },
      selectedContent: 'prospect-data',
      extendedSimulation: undefined,
      message: {},
      interactionFields: leadInteractionFields(this),
      loadingInteractions: false,
      prospectStatusList: [{ slug: null, name: this.$t('null') }],
      subStatusMap: {},
      prospectStatusLoading: true
    };
  },
  async mounted() {
    try {
      this.isLoading = true;
      this.showSimulationButton = (await getFeatureToggleByName('internal-frontend-comercial-manager-show-simulation-button')).isActive;
      await this.loadProspect();
      await this.loadProspectStatus();
      this.simulations = await getSimulations(this.prospectId);
      this.updateStatusField(this.prospect.status);
      this.setSubStatusMap();
      this.updateSubStatusField(this.prospect.status, this.prospect.subStatus);
      this.setRemindersActive(this.prospect.remindersActive);
    } catch (_error) {
      this.message = {
        message: this.$t('errorFetchingData'),
        icon: 'data_maintenance.svg'
      };
    }
    this.isLoading = false;
  },
  methods: {
    statusFieldOnChange(newValue) {
      this.updateSubStatusField(newValue);
    },
    setRemindersActive(inputReminderActive) {
      const reminderActiveField = this.interactionFields.find(
        ({ name }) => name === 'remindersActive'
      );
      reminderActiveField.defaultValue = inputReminderActive;
    },
    buildSimulatorFrontUrl() {
      const payload = {
        brokerId: this.prospect.brokerId,
        prospectId: this.prospect._id
      };
      const base64Payload = convertObjectToBase64(payload);
      return `${process.env.VUE_APP_SIMULATOR_FRONTEND_BR}/br/simulation?payload=${base64Payload}`;
    },
    async createNewInteraction(interactionToCreateIn) {
      const interactionToCreate = { ...interactionToCreateIn };
      interactionToCreate.createdBy = this.$store.state.currentUser.user._id;
      try {
        await addInteractions(this.prospectId, interactionToCreate);
        this.updateStatusField(interactionToCreate.status);
        this.updateSubStatusField(
          interactionToCreate.status,
          interactionToCreate.subStatus
        );
        this.setRemindersActive(interactionToCreate.remindersActive);
      } catch (_error) {
        this.launchToast(this.$t('error-adding-interaction'), 'is-danger');
        return;
      }
      this.$emit('new-interaction-successfully-created');
    },
    async loadProspectStatus() {
      this.prospectStatusLoading = true;
      try {
        this.prospectStatusList = await getProspectStatusList();
      } catch (_error) {
        this.message = {
          message: this.$t('errorFetchingData'),
          icon: 'data_maintenance.svg'
        };
      }
      this.prospectStatusLoading = false;
    },
    updateStatusField(inputStatus) {
      const statusField = this.interactionFields.find(
        ({ name }) => name === 'status'
      );
      statusField.defaultValue = inputStatus;
      statusField.possibleOptions = this.prospectStatusList.reduce(
        (accum, { name, nextAllowedStatus }) => {
          if (name === inputStatus) {
            const currentStatusSlugs = [];
            currentStatusSlugs.push(name, ...nextAllowedStatus);
            accum.push(
              ...currentStatusSlugs.map(status => ({
                slug: status,
                name: this.$t(`prospectStatus.${status}`)
              }))
            );
          }
          return accum;
        },
        []
      );
    },
    setSubStatusMap() {
      this.subStatusMap = this.prospectStatusList.reduce(
        (accumIn, { name, subStatus }) => {
          const accum = { ...accumIn };
          const subStatusArray = subStatus.length > 0
            ? subStatus.map(subSt => ({ slug: subSt, name: this.$t(`prospectSubStatus.${subSt}`) }))
            : [{ slug: 'null', name: this.$t(`prospectSubStatus.${'null'}`) }];
          accum[name] = subStatusArray;
          return accum;
        },
        {}
      );
    },
    goToSingleBrokerView(brokerId) {
      this.$router.push(`/broker/${brokerId}`);
    },
    updateSubStatusField(inputStatus, inputSubStatus = undefined) {
      const subStatusField = this.interactionFields.find(
        ({ name }) => name === 'subStatus'
      );
      subStatusField.possibleOptions = this.subStatusMap[inputStatus];

      const currentDefaultValue = inputSubStatus || (subStatusField.possibleOptions.length > 0 ? subStatusField.possibleOptions[0].slug : null);
      subStatusField.defaultValue = currentDefaultValue;
    },
    async loadProspect() {
      this.loadingInteractions = true;
      try {
        this.prospect = await getProspectById(this.prospectId);
      } catch (_error) {
        this.message = {
          message: this.$t('errorFetchingData'),
          icon: 'data_maintenance.svg'
        };
        return;
      }
      this.loadingInteractions = false;
    },
    dateToFormatedString: val => dateToFormatedString(val),
    formatNumber(number, usePrefix = true) {
      return formatNumberByCountry({ number, countryIn: this.locale(), usePrefix });
    },
    launchToast(message, type = 'is-info', duration = 5000) {
      this.$buefy.toast.open({
        position: 'is-top-right',
        message,
        type,
        duration
      });
    }
  }
};
</script>

<style>
button.button[data-v-38789782]{
  width: 100% !important;
  padding:0 15px;
}
button.button[data-v-38789782] span{
  font-size:0.8rem
}
div.header-content[data-v-38789782] {
  display: none;
}
#document-content[data-v-38789782]{
  padding-top: 0;
}
div.internal-content[data-v-38789782] {
  margin: 0 auto;
 width:auto !important;
 max-width: 1100px;
}
.button[data-v-38789782][disabled]{
  opacity: 0.3;
}

.default-prospect-header {
  margin: 20px;
}
h2.prospect {
  display: inline;
}
.creditu-tag {
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  padding: 0px 10px;
  text-align: center;
}
.prospect-broker-tag{
  background: var(--color-nickel);
  color: #fff;
}

.current-status {
  background: #f2fbfb;
  color: var(--primary);
  padding: 0.3rem 1rem;
  border-radius: 50rem;
  line-height: 1.5;
}

.call-to-action {
  max-width: fit-content;
}
@media (max-width: 860px) {
  .call-to-action {
  max-width: 100%;
}
  .call-to-action .card-item {
  flex-direction: column;
  align-items: center;
}
  .current-status {
    width: 100%;
    margin: 1rem 0;
    display: block;
  }
  #return-btn {
    position: absolute;
    top: 1rem;
    right: 2rem;
  }
}
@media(max-width: 480px){
    .header-name{
    display: none;
  }
  .tabs.is-fullwidth ul {
    display: flex;
    flex-direction: column;
  }
  .tabs.is-fullwidth ul li, .tabs.is-fullwidth ul li.is-active {
    width: 100%;
  }
}
@media (max-width:420px){
  .card-item.document-view[data-v-38789782]{
    padding:0;
    position: relative;
    margin-left: -15px;
    width: 112%;
}
  div.mb-5.section-document[data-v-38789782]{
    display: flex;
    flex-direction: column;
    align-content: space-around;
    align-items: center;
  }
.has-full-border-bottom[data-v-38789782]{
    display:flex;
    align-items:center;
    justify-content:space-between;
  }
  .columns.is-mobile{
    width:100%;
  }
}
</style>

<i18n>
  {
    "cl": {
      "release": "Liberar",
      "back": "Volver",
      "simulationsTitle": "Simulaciones",
      "documentsTitle": "Documentos",
      "contactTitle": "Datos de Contacto",
      "contactMethod": "Medio de contacto",
      "gotAnswerFromClient": "¿Cliente contesta?",
      "isImportant": "¿Es importante?",
      "yes": "Sí",
      "no": "No",
      "null": "No aplica",
      "nextScheduleDate": "Próxima gestión",
      "error-adding-interaction": "Ocurrió un error agregando la nueva interacción",
      "errorFetchingData": "Ocurrió un error al tratar de obtener los datos",
      "prospectStatusSelect": "Estado" ,
      "prospectSubStatusSelect": "Sub-Estado",
      "remindersActive": "Recordatorio automático"
    },
    "pe": {
      "release": "Liberar",
      "back": "Volver",
      "simulationsTitle": "Simulaciones",
      "contactTitle": "Datos de Contacto",
      "documentsTitle": "Documentos",
      "contactMethod": "Medio de contacto",
      "gotAnswerFromClient": "¿Cliente contesta?",
      "isImportant": "¿Es importante?",
      "yes": "Sí",
      "no": "No",
      "null": "No aplica",
      "nextScheduleDate": "Próxima gestión",
      "error-adding-interaction": "Ocurrió un error agregando la nueva interacción",
      "errorFetchingData": "Ocurrió un error al tratar de obtener los datos",
      "prospectStatusSelect": "Estado" ,
      "prospectSubStatusSelect": "Sub-Estado",
      "remindersActive": "Enviar recordatorio",
      "remindersActive": "Recordatorio automático"
    },
    "br": {
      "accessBroker":"Ver Broker",
      "release": "Lançamento",
      "back": "Retorna",
      "simulationsTitle": "Simulações",
      "contactTitle": "Dados de Contacto",
      "documentsTitle": "Documentação",
      "contactMethod": "Meio de contato",
      "gotAnswerFromClient": "Cliente atendeu?",
      "isImportant": "É importante?",
      "null": "Não se aplica",
      "nextScheduleDate": "Data da próxima interação",
      "error-adding-interaction": "Ocurrió un error agregando la nueva interação",
      "errorFetchingData": "Ocorreu um erro ao tentar obter os dados",
      "yes": "Sí",
      "no": "No",
      "prospectStatusSelect": "Status" ,
      "prospectSubStatusSelect": "Sub-Status",
      "remindersActive": "Lembrete automático",
      "simulator": "Nova simulação",
      "simulatorLead": "Deseja simular novamente para esse lead?"
    }
  }
  </i18n>
